// UserDashboard.jsx

import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack'; // Icon remains
import UserDashboardStyles from "./UserDashboard.styles";
import UserDashboardTabs from "./UserDashboardTabs";
import { useNavigate } from "react-router-dom";
import useViewingStatus from "../../hooks/useViewingStatus";
import useSavedListings from "../../hooks/useSavedListings";
import UserViewingRequest from "./UserViewingRequest";
import SavedListings from "./SavedListings";
import EditProfileBasic from "../profiles/EditProfileBasic";
import StarIcon from '@mui/icons-material/Star';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import GroupIcon from '@mui/icons-material/Group';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const UserDashboard = () => {
  const [tabValue, setTabValue] = useState(0);
  const navigate = useNavigate();
  const { checkViewingStatus } = useViewingStatus();
  const { savedListings, loading: loadingSavedListings } = useSavedListings();
  const [scheduledViewings, setScheduledViewings] = useState([]);
  const [loadingViewings, setLoadingViewings] = useState(true);

  useEffect(() => {
    const fetchViewings = async () => {
      try {
        const viewings = await checkViewingStatus();
        setScheduledViewings(viewings);
      } catch (error) {
        console.error("Error loading viewings:", error);
      } finally {
        setLoadingViewings(false);
      }
    };
    fetchViewings();
  }, [checkViewingStatus]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleBackToProperties = () => {
    navigate('/listings');
  };

  return (
    <Box sx={{ padding: '20px' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
        <Button
          variant="contained"
          onClick={handleBackToProperties}
          startIcon={<ArrowBackIcon />}
          sx={{
            borderRadius: '25px',
            textTransform: 'none',
            padding: '10px 24px',
            fontSize: '16px',
            fontWeight: '500',
            background: 'linear-gradient(45deg, #81c241, #81c241)', // Green gradient
            boxShadow: '0px 6px 15px rgba(129, 194, 65, 0.3)', // Green-tinted shadow
            color: '#fff', // White text for contrast
            '&:hover': {
              background: 'linear-gradient(45deg, #6aa832, #81c241)', // Darker green on hover
              boxShadow: '0px 8px 20px rgba(129, 194, 65, 0.4)', // Enhanced green shadow
            },
            transition: 'all 0.3s ease-in-out',
          }}
        >
          Back to Properties
        </Button>
      </Box>
      <UserDashboardTabs tabValue={tabValue} onTabChange={handleTabChange}>
        {tabValue === 0 && (
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Paper sx={UserDashboardStyles.overviewCard}>
                <Typography variant="h6">Upcoming Scheduled Viewings</Typography>
                <Typography variant="h3">{scheduledViewings.length}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={UserDashboardStyles.overviewCard}>
                <Typography variant="h6">Saved Listings</Typography>
                <Typography variant="h3">{savedListings.length}</Typography>
              </Paper>
            </Grid>
          </Grid>
        )}
        {tabValue === 1 && (
          <UserViewingRequest
            scheduledViewings={scheduledViewings}
            loading={loadingViewings}
          />
        )}
        {tabValue === 2 && (
          <SavedListings savedListings={savedListings} loading={loadingSavedListings} />
        )}
        {tabValue === 3 && (
          <Box>
            <Typography variant="h5" sx={{ marginBottom: 2 }}>
              Account Settings
            </Typography>
            <EditProfileBasic />
          </Box>
        )}
        {tabValue === 4 && (
          <Box sx={{ py: 2 }}>
            <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>

              {/* Header */}
              <Box sx={{ bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider', px: 3, pt: 2.5, pb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                  <StarIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="overline" color="text.secondary">Tenant Premium</Typography>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 500, mb: 0.75 }}>
                  Unlock smart access & everyday perks
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Available once your lease is signed. Everything you need to manage access and save on daily spending.
                </Typography>
              </Box>

              {/* Feature grid */}
              <Box sx={{ px: 3, py: 2, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 1.5 }}>
                {[
                  { icon: <LockOpenIcon />, color: 'info', title: 'Guest access codes', desc: 'Generate one-time codes for visitors — no key sharing needed.' },
                  { icon: <AccessTimeIcon />, color: 'success', title: 'Scheduled vendor access', desc: 'Set time-limited windows for cleaners, repair crews, and deliveries.' },
                  { icon: <LocalOfferIcon />, color: 'warning', title: 'Retail & dining savings', desc: 'Exclusive discounts at thousands of popular stores and restaurants near you.' },
                  { icon: <GroupIcon />, color: 'info', title: 'Recurring access', desc: 'Set repeating schedules for trusted people without re-issuing codes.' },
                ].map(({ icon, color, title, desc }) => (
                  <Box key={title} sx={{ display: 'flex', gap: 1.5, p: 1.5, bgcolor: 'action.hover', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ width: 32, height: 32, borderRadius: 1.5, bgcolor: `${color}.lighter`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, '& svg': { fontSize: 16, color: `${color}.main` } }}>
                      {icon}
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>{title}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5, display: 'block' }}>{desc}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>

              {/* Footer */}
              <Box sx={{ px: 3, pb: 3, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 380 }}>
                  No setup fees — cancel anytime from your dashboard.
                </Typography>
                <Button
                  variant="contained"
                  href="/tenant-login"
                  endIcon={<ArrowForwardIcon />}
                  sx={{ bgcolor: '#81c241', '&:hover': { bgcolor: '#6aa832' }, borderRadius: 2, textTransform: 'none', fontWeight: 500 }}
                >
                  Access premium
                </Button>
              </Box>

            </Paper>
          </Box>
        )}
      </UserDashboardTabs>
    </Box>
  );
};

export default UserDashboard;